import { withPeriod } from './view-period';
import { DrrValue } from './drr-value';
import { profileStartup } from '../diagnostics/startup-profile';
import { beginStartupWork, finishStartupWork, startupRowWidthKey } from '../diagnostics/startup-work';
import { Fragment, memo, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from '../ui/icons';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { refreshCampaignSettings } from './refresh-settings';
import type { CampaignRef } from '../api/types';
import type { SortKey } from '../pages/campaign-view';
import { Controls, FinishCampaign } from './controls';
import { ProductStrip } from './thumb';
import { toneOf, type State } from './status';
import { знаками } from '../ui/chisla';
import { useЦели } from '../pages/celi';
import { DeferredBreakdown as Breakdown } from './deferred-details';
import { Spark, SPARK_WIDTH } from './spark';
import { Modal } from '../ui/modal';
import { placeBelow } from '../ui/place';
import { Switch } from '../ui/switch';
import type { ДействиеОкна } from './deystvie-okna';
import { DeferredTopup as Topup } from './deferred-details';
import { TopupNow } from './topup-now';
import { DeferredStopRules as StopRules } from './deferred-details';
import { DeferredHeatSchedule as HeatSchedule } from './deferred-details';
import { campaignType } from './campaign-type';
import { IconCap, IconChevron, IconClock, IconPlus, IconRefresh, IconTopup } from '../ui/icons';
import type { Period } from '../pages/period';
import { K } from '../kabinet/atoms';
import { useВысотаДоНиза } from '../ui/vysota-vida';
import { МеткаГруппы } from '../pages/gruppy';
import { Прочерк } from '../ui/nothing';
import { Спп, ЗапасКапсула } from './zapas';
import { МеткиТестов } from './metki-testov';
import {
  COLUMNS,
  median,
  paintOf,
  readCols,
  saveCols,
  вПорядке,
  type Col,
  type ColKey,
  заголовокГруппы,
} from './columns';

/**
 * Таблица кампаний.
 *
 * ПОЧЕМУ ТАБЛИЦА, А НЕ СТРОКИ. Список карточек отвечал на вопрос «что
 * с этой кампанией» и совсем не отвечал на «какая из сорока хуже всех».
 * А второй вопрос и есть работа: реклама ведётся сравнением, и сравнивать
 * числа глазами можно только когда они стоят столбиком друг под другом.
 * Раскладка взята из трекера, где она прожила год.
 *
 * ПЕРВЫЙ СТОЛБЕЦ ОСТАЁТСЯ ПРЕЖНИМ. Галочка, кнопки пуска-паузы, тумблер
 * управления, фотографии, название, состояние и значки настроек — всё
 * на своих местах. Менялось то, что справа: вместо одной строки денег
 * появились столбцы, которые можно сравнивать.
 *
 * ЧИСЛА КРАСЯТСЯ ОТНОСИТЕЛЬНО МЕДИАНЫ КАБИНЕТА, а не по порогам
 * из головы. «ДРР 12 %» — это хорошо или плохо? Ответ зависит от товара,
 * от сезона и от того, чем живёт продавец. А вот «вдвое хуже, чем
 * остальные ваши кампании» — это ответ, годный без оговорок.
 */

const money = new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 });
const money2 = new Intl.NumberFormat('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ints = new Intl.NumberFormat('ru-RU');

/**
 * Сколько столбцов стоит слева от настраиваемых.
 *
 * ОДИН: «Кампания». Фотографии и динамика стояли рядом с ним прибитыми
 * к разметке — их было три, и число 3 было вписано в `colSpan` строк
 * разбора вручную. Когда они стали колонками, строка разбора поехала
 * бы на два столбца вправо, а заметить это можно было только раскрыв
 * разбор глазами. Теперь число одно и лежит рядом с местом,
 * где рисуется сам столбец.
 */
const LEAD_COLS = 1;

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЧТО ШИРЕ ЭКРАНА — ТО ЛИПНЕТ К ЭКРАНУ, А НЕ ЕДЕТ С ТАБЛИЦЕЙ.
 *
 * Кнопка разбора и сам разбор относятся к СТРОКЕ, а не к столбцу,
 * и в эталоне они прибиты к левому краю ВИДИМОЙ области и растянуты
 * на её ширину:
 *
 *     position: sticky; left: 0; width: Math.max(320, s.w - 42)
 *
 * У нас они стояли по центру ВСЕЙ таблицы. Пока столбцов тринадцать
 * из двадцати одного, таблица шире окна на две-три сотни пикселей —
 * и «по центру таблицы» это правее центра экрана. Сдвинь таблицу вбок,
 * и кнопка уезжает совсем: она ищется глазами по центру, а её там нет.
 *
 * Ширину даёт наблюдатель, а не замер при отрисовке: полоса меняется
 * от сворачивания колонки групп, от выбора столбцов и от окна.
 * ═══════════════════════════════════════════════════════════════════════
 */
function поВиду(ширина: number | null): React.CSSProperties {
  if (ширина === null) return {};
  return { position: 'sticky', left: 0, width: Math.max(320, ширина) };
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ГИСТЕРЕЗИС ЗАМЕРА ШИРИНЫ — ИНАЧЕ СТРАНИЦА ВСТАЁТ НА ДРОБНОМ ЗУМЕ.
 *
 * ЧТО БЫЛО. Наблюдатель писал ширину в состояние на КАЖДОЕ изменение
 * больше одной точки. Появление и исчезновение горизонтальной полосы
 * прокрутки меняет ширину примерно на пятнадцать точек — и получался
 * круг: замер → состояние → перерисовка → новая ширина → замер.
 *
 * На целом масштабе круг размыкался: вторая ширина совпадала с первой
 * до точки, и `setState` возвращал прежнее значение. НА ДРОБНОМ
 * (зум браузера 110 %, превью, множители экрана) не размыкался никогда:
 * округление давало то 1119, то 1120, и страница вставала намертво.
 * Найдено дизайнером 28.08.2026 при проверке эталона.
 *
 * ЧТО СТАЛО. Состояние обновляется в двух случаях:
 *
 *   • сменился ПОРОГ раскладки (900 · 1120) — это меняет вид, и знать
 *     об этом обязательно, каким бы мелким ни был шаг;
 *   • ширина уехала не меньше чем на 24 точки — заведомо больше
 *     полосы прокрутки, то есть это настоящее изменение окна.
 *
 * Значение округляется до восьми точек: дробный масштаб перестаёт
 * порождать соседние целые, и круг не может замкнуться в принципе.
 *
 * ПОЧЕМУ НЕ ПРОСТО «ПОБОЛЬШЕ ПОРОГ». Порог без округления оставляет
 * дрожание на границе: 1100 → 1124 → 1100 отличаются на 24 каждый раз.
 * Округление без порога не спасает от полосы прокрутки: 15 точек
 * иногда перескакивают через восьмёрку. Нужны оба.
 * ═══════════════════════════════════════════════════════════════════════
 */
export const ПОРОГИ_ШИРИНЫ = [900, 1120] as const;
export const ШАГ_ШИРИНЫ = 24;
export const ОКРУГЛЕНИЕ_ШИРИНЫ = 8;

/** В какой полосе раскладки лежит ширина: 0 · 1 · 2. */
export function полосаШирины(w: number): number {
  let i = 0;
  for (const порог of ПОРОГИ_ШИРИНЫ) if (w >= порог) i += 1;
  return i;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ПОЛОСА МЕНЯЕТСЯ С ЗАЛИПАНИЕМ, И БЕЗ ЭТОГО ГИСТЕРЕЗИС НЕ РАБОТАЕТ.
 *
 * Первая сборка этой правки ставила порог только на РАССТОЯНИЕ, а смену
 * полосы пропускала любую — «порог меняет вид, о нём знать обязательно».
 * Звучит верно и оставляет круг открытым: полоса прокрутки шириной
 * пятнадцать точек, стоящая ВЕРХОМ НА ПОРОГЕ, гоняет 1120 ↔ 1104
 * бесконечно. Оба перехода — смена полосы, оба принимаются, и шаг
 * в 24 точки не спрашивается вовсе. Поймано проверкой, которая
 * перебирает все ширины подряд, а не примеры.
 *
 * ЗАЛИПАНИЕ НЕСИММЕТРИЧНО, И В ЭТОМ ВСЯ СУТЬ. Вверх полоса переходит
 * ровно на пороге: раскладка обязана стать широкой там, где сказано.
 * Вниз — только уйдя за порог на те же 24 точки. Тогда возврат наверх
 * требует движения больше, чем даёт полоса прокрутки, и круг
 * не может замкнуться ни на одной ширине.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function сменаПолосы(было: number, стало: number): boolean {
  const b0 = полосаШирины(было);
  const b1 = полосаШирины(стало);
  if (b0 === b1) return false;
  if (стало > было) return true;
  /* Вниз — только за порог с запасом. Порог тот, который отпускаем:
     нижняя граница полосы, в которой мы стояли. */
  const порог = ПОРОГИ_ШИРИНЫ[b0 - 1]!;
  return стало <= порог - ШАГ_ШИРИНЫ;
}

/** Брать ли новый замер. Чистая функция — её и проверяют. */
export function приниматьШирину(было: number | null, стало: number): boolean {
  if (было === null) return true;
  if (Math.abs(было - стало) >= ШАГ_ШИРИНЫ) return true;
  return сменаПолосы(было, стало);
}

/**
 * Ширина видимой полосы таблицы — для кнопки разбора и самого разбора.
 *
 * ВЫСОТА МЕРИТСЯ ОТДЕЛЬНЫМ ХУКОМ (`useВысотаДоНиза`), хотя узел тот же.
 * Правило высоты общее у двух таблиц кабинета, и жить оно должно
 * в одном месте; правило ширины — только здесь, и завязано на полосы
 * раскладки этого списка.
 */
function useШиринаВида(): [React.RefObject<HTMLDivElement | null>, number | null] {
  const реф = useRef<HTMLDivElement>(null);
  const [ширина, setШирина] = useState<number | null>(null);

  useEffect(() => {
    const el = реф.current;
    /* Наблюдателя нет в разборе разметки проверками — там ширины
       и не бывает, и обходиться без неё должно молча. */
    if (el === null || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((es) => {
      const сырое = es[0]!.contentRect.width;
      const w = Math.round(сырое / ОКРУГЛЕНИЕ_ШИРИНЫ) * ОКРУГЛЕНИЕ_ШИРИНЫ;
      setШирина((было) => (приниматьШирину(было, w) ? w : было));
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return [реф, ширина];
}

/**
 * Имя первой группы столбцов — той, что в эталоне не подписана.
 *
 * Строкой, а не признаком в `columns.ts`: признак «не подписывать»
 * читался бы как свойство группы, а это свойство ТАБЛИЦЫ — первая
 * группа стоит над самим названием кампании, и подпись ей не нужна.
 */
const ПЕРВАЯ_ГРУППА = 'Кампания';

/** Ширина списка «Колонки». Число, а не «по содержимому», — см. `ui/place.ts`. */
/**
 * Столбцы по группам — для окна «Какие столбцы показывать».
 *
 * Сетка эталона (`repeat(auto-fit, minmax(180px, 1fr))`) раскладывает
 * группы рядами, и каждой нужен свой столбик; плоский список
 * с заголовками между строк для такой сетки не годится.
 */
export function группыПоПорядку(порядок: readonly ColKey[]): Array<{ имя: string; столбцы: Col[] }> {
  const поКлючу = new Map(COLUMNS.map((c) => [c.key, c]));
  const out: Array<{ имя: string; столбцы: Col[] }> = [];
  for (const k of порядок) {
    const c = поКлючу.get(k);
    if (c === undefined) continue;
    const имя = c.group ?? '';
    const последняя = out[out.length - 1];
    if (последняя !== undefined && последняя.имя === имя) последняя.столбцы.push(c);
    else out.push({ имя, столбцы: [c] });
  }
  return out;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ГРУППЫ СЧИТАЮТСЯ ПО ПОРЯДКУ, А НЕ ЗАДАНЫ РАЗ НАВСЕГДА.
 *
 * 08.09.2026 владелец попросил перетаскивание. Стоило столбцу переехать
 * — и постоянный список групп начал бы врать: в окне графа осталась бы
 * под прежним заголовком, а в таблице встала бы посреди чужой группы.
 *
 * Таблица и без того считает строку групп по ПОДРЯД ИДУЩИМ столбцам
 * с одинаковым `group` (`groups` ниже). Окно теперь считает так же —
 * значит показывают они одно и то же, что бы человек ни перетащил.
 * ═══════════════════════════════════════════════════════════════════ */
export const ГРУППЫ_СТОЛБЦОВ = группыПоПорядку(COLUMNS.map((c) => c.key));

export interface Row {
  campaign: CampaignRef;
  state: State;
}

/**
 * Значение столбца у кампании.
 *
 * ОДИН СЛОВАРЬ НА ВЕСЬ ЭКРАН: по нему и рисуется ячейка, и считается
 * медиана, и сортируется таблица. Разведи их — и столбец начнёт
 * сортироваться не по тому, что в нём написано.
 */
export function cellValue(c: CampaignRef, key: ColKey): number | null {
  switch (key) {
    // НАСТРОЙКИ ЧИСЛОМ НЕ ВЫРАЖАЮТСЯ и потому не сортируются:
    // «включено» не больше и не меньше «выключено».
    case 'rules':
      return null;
    case 'budget':
      return c.balance;
    case 'spend':
      return c.spend;
    case 'views':
      return c.views;
    case 'clicks':
      return c.clicks;
    case 'ctr':
      return c.ctr;
    case 'atbs':
      return c.atbs;
    case 'crCart':
      return c.crCart;
    case 'orders':
      return c.orders;
    case 'crOrder':
      return c.crOrder;
    case 'revenue':
      return c.revenue;
    case 'drr':
      return c.drr;
    case 'cpo':
      return c.cpo;
    case 'cpc':
      return c.cpc;
    case 'canceled':
      return c.canceled;
    case 'spendYest':
      return c.spendYest;
    // ВИЛКА СОРТИРУЕТСЯ И КРАСИТСЯ ПО ВЕРХНЕМУ КРАЮ; само число
    // рисует своя ячейка — она умеет показать «от и до».
    case 'cpmBid':
      return c.cpmMax;
    // Своими ячейками не сортируют: полоска фотографий, столбики
    // динамики и значки правил числом не выражаются.
    case 'products':
    case 'spark':
      return null;
    case 'limitDay':
      /**
       * ЛИМИТ ПОКАЗЫВАЕТСЯ, ТОЛЬКО ПОКА ОН ДЕЙСТВУЕТ.
       *
       * Выключенный тумблер при заданном числе — обычное состояние:
       * человек снял ограничение, не стирая порог, чтобы вернуть его
       * завтра. Показать это число значило бы утверждать, что расход
       * ограничен, — а он нет.
       */
      return c.features.limits.on ? c.features.limits.day : null;
  }
}

/**
 * Ставка кампании: одно число или вилка.
 *
 * ВИЛКА, А НЕ СРЕДНЕЕ И НЕ НАИБОЛЬШЕЕ. Ставка у Wildberries принадлежит
 * паре «товар + площадка», а не кампании. Когда все они равны — в
 * ячейке одно число, и так в подавляющем большинстве строк. Когда нет,
 * человеку надо знать именно это: «255» там, где половина товаров
 * стоит на 90, отправит его поднимать не ту ставку.
 *
 * ПРОЧЕРК ОЗНАЧАЕТ «СТАВОК НЕ ЗАДАНО ВОВСЕ» — и это же ответ
 * на вопрос, почему кампания числится активной и не тратит.
 */
export function bidText(min: number | null, max: number | null): string | null {
  /**
   * ПУСТО ВОЗВРАЩАЕТСЯ `null`, А НЕ ПРОЧЕРКОМ. Прочерк без причины —
   * это молчание (ответ 32), а причина здесь не общая: «ставок не
   * задано вовсе» — это же ответ на вопрос, почему кампания числится
   * активной и не тратит. Рисует его `Прочерк` в самой ячейке.
   */
  if (min === null || max === null) return null;
  // БЕЗ КОПЕЕК, в отличие от остальных денег в таблице. Ставку задают
  // целыми рублями, и «255,00» рядом с «90,00» — это две лишние пары
  // цифр в каждой строке ради нулей, которые никогда не меняются.
  return min === max ? money.format(max) : `${money.format(min)} — ${money.format(max)}`;
}

/** Ставка в клетке: число, вилка или прочерк с причиной. */
export function BidCellText({ min, max }: { min: number | null; max: number | null }) {
  const text = bidText(min, max);
  return text === null ? (
    <Прочерк почему="Ставок у кампании нет вовсе — поэтому она числится активной и не тратит" />
  ) : (
    <>{text}</>
  );
}

/**
 * ПРОЧЕРК, А НЕ НОЛЬ. «Заказов нет» и «мы не знаем, сколько заказов» —
 * разные утверждения, и нолём их не свести: человек прочтёт ноль как
 * ответ и пойдёт чинить кампанию, у которой всё в порядке.
 *
 * ═══════════════════════════════════════════════════════════════════════
 * У ПРОЧЕРКА ЕСТЬ ПРИЧИНА, И ОНА ПРИХОДИТ ИЗ СТОЛБЦА (ответ 32).
 *
 * Прежде здесь возвращалась голая строка «—» — одна и та же на все
 * двадцать столбцов. Прочерк в «Лимите» значит «не задан», прочерк
 * в «Заказах» значит «статистика ещё не выгружена», а прочерк в «ДРР»
 * значит «делить не на что». Три разных ответа выглядели одинаково,
 * и человек читал их как один: «сломалось».
 *
 * Поэтому теперь возвращается узел, а не строка: у пустого — подсказка
 * `col.пусто`.
 * ═══════════════════════════════════════════════════════════════════════
 */
/**
 * ═══════════════════════════════════════════════════════════════════════
 * ЗНАКОВ ПОСЛЕ ЗАПЯТОЙ — СТОЛЬКО, СКОЛЬКО ПРОСИТ СТОЛБЕЦ.
 *
 * Здесь стояло правило «меньше десяти тысяч — с копейками, больше —
 * без», одно на все деньги, и `toFixed(2)` на все проценты. Из-за
 * первого расход в 12 480,00 ₽ печатался как «12 480», а соседний
 * в 3 210,00 — с копейками: один столбец, две записи. Из-за второго
 * ДРР в 1 101 % выходил «1101,00 %» — и без разделителя тысяч,
 * потому что `toFixed` его не ставит.
 *
 * Теперь число знаков спрашивается у столбца (`col.знаков`), а печатает
 * всегда `Intl`. Довод по каждому столбцу — в `columns.ts`, у `ColЗнаков`.
 *
 * СОТНЯ ПРОЦЕНТОВ ОТМЕНЯЕТ ДОЛЮ. «1 101,0 %» обещает точность, которой
 * в таком числе нет и быть не может, а места занимает как настоящая.
 * Так и в эталоне: «18,4 %», «9,4 %», но «1 101 %».
 * ═══════════════════════════════════════════════════════════════════════
 */
/* ФОРМАТ БЕРЁТСЯ ГОТОВЫМ, А НЕ СОБИРАЕТСЯ НА КАЖДУЮ ЯЧЕЙКУ. Здесь
   стояло `new Intl.NumberFormat` внутри функции, и она зовётся из
   `число()` на каждый процент и каждую дробную сумму: полсотни строк
   на восьми числовых столбцах — две сотни сборок формата за одну
   перерисовку, а перерисовок в этой таблице много (наведение,
   сортировка, отбор, фоновое обновление). Замер: 32,5 мкс на сборку
   против 0,7 мкс на само форматирование. Довод целиком — в `chisla.ts`. */
const дробью = знаками;

function число(v: number, col: Pick<Col, 'kind' | 'знаков' | 'хвост'>): string {
  const хвост = col.хвост ?? '';
  if (col.kind === 'int') return ints.format(Math.round(v)) + хвост;
  if (col.kind === 'percent') {
    const знаков = Math.abs(v) >= 100 ? 0 : (col.знаков ?? 2);
    return `${дробью(знаков).format(v)} %`;
  }
  const знаков = col.знаков ?? 2;
  return (знаков === 2 ? money2.format(v) : дробью(знаков).format(v)) + хвост;
}

function render(v: number | null, col: Pick<Col, 'kind' | 'знаков' | 'хвост' | 'пусто'>) {
  return v === null ? <Прочерк почему={col.пусто} /> : <>{число(v, col)}</>;
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ВЫБОР СТОЛБЦОВ — СВОЙ УЗЕЛ В РЯДУ ОТБОРА, А НЕ ЖИЛЕЦ ТАБЛИЦЫ.
 *
 * До 27.08 кнопка «Колонки» жила ВНУТРИ `CampaignTable` и уезжала
 * в ряд отбора порталом через переданный узел (`slot`). Довод был
 * «выбор столбцов принадлежит таблице: она его помнит и по нему
 * рисует», и он держался ровно до четырёх экранов эталона.
 *
 * ЧТО ЕГО СЛОМАЛО. `грузится`, `отказ данных`, `нет ключа` рисуют ряд
 * отбора ЦЕЛИКОМ — с поиском, чипами и «Колонками», — а таблицы там
 * нет вовсе. Портал из несуществующей таблицы кнопку не поставит,
 * и сверка сказала прямо: «кнопка-колонок: НЕТ В ПРОДУКТЕ» на всех
 * четырёх ширинах трёх экранов.
 *
 * Значит, выбор столбцов принадлежит ЭКРАНУ, а не таблице: он
 * переживает и загрузку, и отказ, и отсутствие ключа. Состояние
 * поднято в `home.page.tsx`, таблица получает готовое `on`, а кнопка
 * стоит в ряду отбора сама по себе — без портала и без узла-места.
 * ═══════════════════════════════════════════════════════════════════════
 */
export function ВыборКолонок({
  on,
  flip,
  порядок,
  переставить,
  вернутьКакБыло,
}: {
  on: Record<ColKey, boolean>;
  flip: (k: ColKey) => void;
  /** Порядок столбцов — общий с таблицей, живёт на экране. */
  порядок: readonly ColKey[];
  переставить: (что: ColKey, перед: ColKey) => void;
  вернутьКакБыло: () => void;
}) {
  const [menu, setMenu] = useState(false);
  /* СОСТОЯНИЕ ПЕРЕТАСКИВАНИЯ ЖИВЁТ У ОКНА, А НЕ У СТРОКИ: строке надо
     знать, тащат ли ЕЁ и находится ли курсор НАД НЕЙ, — а это сведения
     о соседях. */
  const [тащим, setТащим] = useState<ColKey | null>(null);
  const [над, setНад] = useState<ColKey | null>(null);
  const cols = COLUMNS.filter((c) => on[c.key]);
  return (
        /* Обёртка не участвует в выкладке: в эталоне кнопка лежит прямо
           в ряду отбора и потому не растягивается. Список групп всё
           равно рисуется порталом в корень страницы, и `relative`
           здесь никому не нужен — см. пояснение выше. */
        <div style={{ display: 'contents' }}>
          {/* 32, как в эталоне: та же высота, что у чипов отбора над ней.
              Тридцать выбивалось из ряда на пару пикселей — мелочь,
              которая на снимке владельца читается как «поехало». */}
          <button
            className={K.кнопкаКолонок}
            data-d="кнопка-колонок"
            aria-expanded={menu}
            onClick={() => setMenu(!menu)}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <rect x="2.2" y="2.6" width="11.6" height="10.8" rx="2" />
              <path d="M6.3 2.6v10.8M9.9 2.6v10.8" />
            </svg>
            Колонки
            {/* ═══════════════════════════════════════════════════════
                СЧИТАЕТСЯ И СТОЛБЕЦ КАМПАНИИ — тот, что нельзя убрать.

                В эталоне на кнопке «Колонки 13 из 21», и тринадцать
                там ВМЕСТЕ с названием кампании: человек считает
                столбцы глазами, а не по нашему списку настроек.
                Пока мы считали только настраиваемые, выходило
                «12 из 20» — число, которое не сходится с тем,
                что он видит. */}
            <span style={{ color: 'var(--ink-3)' }}>
              {cols.length + 1} из {COLUMNS.length + 1}
            </span>
          </button>
          {/* ═══════════════════════════════════════════════════════════
              ОКНО, А НЕ ВЫПАДЕНИЕ ИЗ КНОПКИ — ТАК В ЭТАЛОНЕ.

              Здесь висела панель шириной 260, приколоченная к кнопке:
              двадцать один столбец в один узкий столбик, с прокруткой
              внутри, а на телефоне — почти во весь экран, но всё равно
              узкой полосой.

              У эталона это окно поверх страницы: затемнение
              (`--scrim`), окно до 760 в ширину, шапка «Какие столбцы
              показывать · включено 13 из 21», сетка групп
              (`repeat(auto-fit, minmax(180px, 1fr))`) и подвал
              с доводом и кнопкой «Готово». Группы встают рядами
              по три-четыре, и весь список виден разом.

              Ни сверка, ни глаз этого не находили: состояние «окно
              колонок» лежало в замере эталона снятым и не читалось
              ни разу. Нашлось в тот день, когда его подключили.

              ИМЯ УЗЛА СТОИТ НА ЗАТЕМНЕНИИ, а не на самом окне: так
              у эталона (`data-d` на `position:fixed;inset:0`).
              У «окна целей» он же стоит на окне — стенд в этом
              непоследователен, и повторяем мы его, а не себя.
              ═══════════════════════════════════════════════════════ */}
          {menu &&
            createPortal(
              <div
                data-kab
                data-d="окно-колонок"
                className="rk-cols-scrim"
                onClick={() => setMenu(false)}
              >
                <div className="rk-cols-window" role="dialog" aria-modal="true" aria-label="Какие столбцы показывать" onClick={(e) => e.stopPropagation()}>
                  <div className="rk-cols-head">
                    <b className="rk-text-h2">Какие столбцы показывать</b>
                    <span className="rk-text-sm rk-faint">
                      включено {cols.length + 1} из {COLUMNS.length + 1}
                    </span>
                    <button
                      type="button"
                      aria-label="Закрыть"
                      className="rk-cols-close"
                      onClick={() => setMenu(false)}
                    >
                      <IconClose size={14} />
                    </button>
                  </div>

                  {/* ═══════════════════════════════════════════════════
                      ГРУППЫ — ПО ТЕКУЩЕМУ ПОРЯДКУ, а не по заведённому
                      раз навсегда: иначе после первого же переноса окно
                      показывало бы одно, а таблица другое.
                      ═══════════════════════════════════════════════ */}
                  <div className="rk-cols-grid">
                    {группыПоПорядку(порядок).map((г, гi) => (
                      <div
                        key={`${г.имя}-${гi}`}
                        className="flex flex-col"
                        style={{ gap: 2 }}
                      >
                        <span className={K.надписьПодписки} style={{ marginBottom: 4 }}>
                          {г.имя}
                        </span>
                        {г.столбцы.map((c) => (
                          <ГрафаКампаний
                            key={c.key}
                            c={c}
                            видна={on[c.key]}
                            onToggle={flip}
                            тащим={тащим}
                            над={над}
                            setТащим={setТащим}
                            setНад={setНад}
                            onMove={переставить}
                          />
                        ))}
                      </div>
                    ))}
                  </div>

                  <div className="rk-cols-foot">
                    <span className="rk-text-sm rk-faint">
                      Десять столбцов помещаются в 1440 без прокрутки. Ручка слева
                      перетаскивает столбец.
                    </span>
                    {/* «ВЕРНУТЬ КАК БЫЛО» — И НАБОР, И ПОРЯДОК ОДНОЙ КНОПКОЙ,
                        как в кластерах (ТЗ 02.09, раздел 2.1). Человек,
                        у которого таблица стала непонятной, не разбирается,
                        что он испортил, — он хочет обратно ту, которую ему
                        показали в первый раз. */}
                    <button
                      type="button"
                      className="rk-button-quiet"
                      data-d="кнопка-вернуть-столбцы"
                      onClick={вернутьКакБыло}
                    >
                      Вернуть как было
                    </button>
                    <button type="button" className="rk-button" onClick={() => setMenu(false)}>
                      Готово
                    </button>
                  </div>
                </div>
              </div>,
              document.body,
            )}
    </div>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════════
 * ОДНА СТРОКА ОКНА СТОЛБЦОВ: РУЧКА + ГАЛОЧКА.
 *
 * 08.09.2026 владелец: «в общей таблице кампаний сделать колонки как
 * в кластерах, с возможностью перетягивания».
 *
 * УСТРОЙСТВО ВЗЯТО У КЛАСТЕРОВ СЛОВО В СЛОВО (`ГрафаВМеню`
 * в `clusters/clusters.page.tsx`), и это нарочно: два меню столбцов
 * в одном кабинете, которые тащатся по-разному, — это два разных
 * кабинета.
 *
 * ВСЯ СТРОКА `draggable`, А НЕ ОДНА РУЧКА. Так в эталоне; ручка —
 * это ЗНАК, что строку можно нести, а не единственное место, за которое
 * берутся. Галочка при этом стоит у другого края, и спорить им негде.
 *
 * ПОЛОСКА СВЕРХУ ПОКАЗЫВАЕТ, КУДА ВСТАНЕТ. Без неё перетаскивание
 * вслепую: отпустил — и смотришь в таблицу, угадал или нет.
 * ═══════════════════════════════════════════════════════════════════════
 */
function ГрафаКампаний({
  c,
  видна,
  onToggle,
  тащим,
  над,
  setТащим,
  setНад,
  onMove,
}: {
  c: Col;
  видна: boolean;
  onToggle: (k: ColKey) => void;
  тащим: ColKey | null;
  над: ColKey | null;
  setТащим: (v: ColKey | null) => void;
  setНад: (v: ColKey | null) => void;
  onMove: (что: ColKey, перед: ColKey) => void;
}) {
  const тащатЭту = тащим === c.key;
  const мишень = над === c.key && тащим !== null && !тащатЭту;

  return (
    <div
      data-d="графа-кампаний"
      data-graf={c.key}
      draggable
      onDragStart={(e) => {
        setТащим(c.key);
        /* Перетаскивание без данных Firefox не начинает вовсе. */
        try {
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', c.key);
        } catch {
          /* строгие настройки приватности — перетаскивание не начнётся */
        }
      }}
      onDragOver={(e) => {
        /* Без этого `drop` не случится: по умолчанию всё запрещено. */
        e.preventDefault();
        if (над !== c.key) setНад(c.key);
      }}
      onDrop={(e) => {
        e.preventDefault();
        if (тащим !== null && тащим !== c.key) onMove(тащим, c.key);
        setТащим(null);
        setНад(null);
      }}
      onDragEnd={() => {
        /* Бросили мимо списка — полоска иначе висела бы до следующего
           касания. */
        setТащим(null);
        setНад(null);
      }}
      className="flex items-center rk-text-sm"
      style={{
        gap: 8,
        height: 28,
        flex: '0 0 auto',
        padding: '0 4px',
        borderRadius: 'var(--r-sm)',
        cursor: 'grab',
        color: 'var(--ink-2)',
        background: тащатЭту ? 'var(--bg-active)' : 'transparent',
        boxShadow: мишень ? 'inset 0 2px 0 0 var(--brand)' : 'none',
        opacity: тащатЭту ? 0.5 : 1,
      }}
    >
      {/* РУЧКА — ТОТ ЖЕ РИСУНОК, ЧТО В КЛАСТЕРАХ: 11×14, шесть точек
          радиусом 1.2, цвет `--line-strong`. */}
      <svg
        width="11"
        height="14"
        viewBox="0 0 11 14"
        fill="currentColor"
        aria-hidden
        style={{ color: 'var(--line-strong)', flex: '0 0 auto' }}
      >
        <circle cx="2.5" cy="3" r="1.2" />
        <circle cx="8.5" cy="3" r="1.2" />
        <circle cx="2.5" cy="7" r="1.2" />
        <circle cx="8.5" cy="7" r="1.2" />
        <circle cx="2.5" cy="11" r="1.2" />
        <circle cx="8.5" cy="11" r="1.2" />
      </svg>
      {/* ГАЛОЧКА В ЯРЛЫКЕ, А ЯРЛЫК НЕ `draggable`: нажатие на неё
          не должно начинать перенос. */}
      <label
        className="flex min-w-0 flex-1 cursor-pointer items-center"
        style={{ gap: 8 }}
        draggable={false}
        onDragStart={(e) => e.preventDefault()}
      >
        <input type="checkbox" checked={видна} onChange={() => onToggle(c.key)} />
        <span className="truncate" title={c.hint}>
          {c.title}
        </span>
      </label>
    </div>
  );
}

export function CampaignTable({
  rows,
  base,
  wsId,
  cabinetId,
  picked,
  onPick,
  onDone,
  sort,
  desc,
  onSort,
  period,
  on,
  порядок,
  именаГрупп,
}: {
  rows: Row[];
  base: string;
  wsId: string;
  cabinetId: string;
  picked: Set<string>;
  onPick: (id: string) => void;
  onDone: (text: string) => void;
  sort: SortKey;
  desc: boolean;
  onSort: (key: SortKey) => void;
  period: Period;
  /**
   * Имена групп по опознавателю — для метки в строке.
   *
   * ГОТОВОЙ КАРТОЙ, А НЕ ЗАПРОСОМ ИЗНУТРИ ТАБЛИЦЫ: таблица рисуется
   * заново на каждое нажатие галочки, и запрос внутри неё жил бы
   * своей жизнью — то есть иногда отставал бы от колонки слева,
   * которая читает те же группы.
   */
  именаГрупп?: Map<string, string>;
  /**
   * Какие столбцы показывать.
   *
   * ГОТОВЫМ НАБОРОМ ОТ ЭКРАНА, А НЕ СВОИМ СОСТОЯНИЕМ. Довод целиком —
   * у `ВыборКолонок` выше: кнопка выбора стоит в ряду отбора и живёт
   * на трёх экранах, где таблицы нет вовсе.
   */
  on: Record<ColKey, boolean>;
  /**
   * Порядок столбцов — тот же, что в окне выбора.
   *
   * ГОТОВЫМ СПИСКОМ ОТ ЭКРАНА, А НЕ СВОИМ ЧТЕНИЕМ ИЗ ХРАНИЛИЩА: два
   * читателя одной записи расходятся ровно на то время, пока один
   * из них не перерисовался, — и человек видит, как столбец переехал
   * в окне и не переехал в таблице.
   */
  порядок: readonly ColKey[];
}) {
  const startupWorkStarted = beginStartupWork();
  /* ЯКОРНОЙ АРИФМЕТИКИ БОЛЬШЕ НЕТ: список столбцов открывается ОКНОМ
     поверх страницы (так в эталоне), а окно стоит по центру и от кнопки
     не зависит. Прежде здесь считалось, куда свесить панель шириной 260,
     общей `placeBelow`. */
  /**
   * Какие кампании раскрыты.
   *
   * НЕСКОЛЬКО СРАЗУ, А НЕ ОДНА. Разбор открывают, чтобы СРАВНИТЬ:
   * «у этой сливает два товара, а у соседней?» Закрывай предыдущую
   * при открытии следующей — и сравнить станет нечего.
   */
  const [openIds, setOpen] = useState<Set<string>>(new Set());
  /**
   * Настройка открывается ОКНОМ ПОВЕРХ ТАБЛИЦЫ, а не переходом внутрь
   * кампании.
   *
   * Переход стоит дорого: экран сменился, место в списке потеряно,
   * обратно — ещё одно нажатие и повторный поиск строки глазами.
   * А правка тут секундная: поменял порог, закрыл, поехал дальше.
   */
  const [setting, setSetting] = useState<{ id: string; which: Which } | null>(null);
  /**
   * ═══════════════════════════════════════════════════════════════════
   * КНОПКА «СОХРАНИТЬ» ЖИВЁТ В ПОДВАЛЕ ОКНА, А НАЖИМАЕТ ЕЁ РАЗДЕЛ.
   *
   * 14.09.2026 владелец: «при нажатии на правила автопополнения,
   * лимитов, я не могу изменить данные и нажать сохранить, кнопки
   * сохранить нет почему-то».
   *
   * ЕЁ И НЕ БЫЛО. Разделы внутри окна рисуются с `bare`, а в этом виде
   * свою кнопку они НЕ рисуют — по уговору: кнопка принадлежит окну,
   * а знает, что набрано и можно ли отправлять, только раздел. Уговор
   * описан в `deystvie-okna.ts`, и карточка кампании (`panel.tsx`) его
   * соблюдает давно. Это окно — её близнец, сделанный для списка, —
   * забыло и подвал, и тумблер: раздел исправно звал `onДействие`,
   * а принять его было некому.
   *
   * ЧТО ЭТО СТОИЛО. Окно открывалось, числа правились, а сохранить
   * их было нечем. Хуже молчащей кнопки только кнопка, которой нет:
   * человек решает, что сломался он.
   * ═══════════════════════════════════════════════════════════════════
   */
  const [действие, setДействие] = useState<ДействиеОкна | null>(null);
  /* ЧИСТИМ ТОЛЬКО ПРИ ЗАКРЫТИИ. Чистить при каждой смене окна нельзя:
     свои действия разделы ставят в своих эффектах, а те выполняются
     РАНЬШЕ родительского — и кнопка гасла бы сразу после появления. */
  useEffect(() => {
    if (setting === null) setДействие(null);
  }, [setting]);
  const qcНастройки = useQueryClient();
  /* ТУМБЛЕР НАСТРОЙКИ — ТЕМ ЖЕ АДРЕСОМ, ЧТО У КАРТОЧКИ. Второй способ
     включить ту же возможность однажды разошёлся бы с первым. */
  const включить = useMutation({
    mutationFn: async (v: { id: string; which: Which; on: boolean }) =>
      api<{ message: string }>(`${base}/campaigns/${v.id}/features/${v.which}`, {
        method: 'PUT',
        body: { on: v.on },
      }),
    onSuccess: async (r, v) => {
      onDone(r.message);
      await refreshCampaignSettings(qcНастройки, base, v.id);
    },
  });
  /* Ширина ВИДИМОЙ полосы таблицы — для кнопки разбора и самого
     разбора; довод целиком у `useШиринаВида`. */
  const [видРеф, видШирина] = useШиринаВида();
  const видВысота = useВысотаДоНиза();
  /**
   * Какой кампании кладём денег руками.
   *
   * ОТДЕЛЬНЫМ СОСТОЯНИЕМ ОТ НАСТРОЕК, а не четвёртым значением в них.
   * Настройки описывают будущее и правятся мимоходом; это окно тратит
   * деньги по нажатию. Общее состояние однажды открыло бы не то окно —
   * и это была бы единственная ошибка такого рода, которая стоит денег.
   */
  const [deposit, setDeposit] = useState<string | null>(null);
  const toggleOpen = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  /* СТОЛБЦЫ В ВЫБРАННОМ ПОРЯДКЕ. Строка групп ниже считается
     по ПОДРЯД идущим столбцам с одинаковым `group` — значит она сама
     подстроится под перенос: группа, разорванная чужим столбцом,
     честно распадётся на две. Это не поломка, а то, что человек
     и выбрал. */
  const cols = useMemo(() => вПорядке(порядок, on), [порядок, on]);

  /* ТОТ ЖЕ ЗАПРОС, ЧТО У МЕТКИ «цель 18 %» в полосе итогов: один ключ
     на страницу, один поход в базу, и одно число в обоих местах.
     Сохранение целей обновляет этот ключ — цвета перекрашиваются
     сразу, не дожидаясь перезагрузки страницы. */
  const цели = useЦели(base);

  /**
   * Медианы считаются по ПОКАЗАННЫМ строкам, а не по всему кабинету.
   *
   * Человек, включивший отбор «не тратят», сравнивает их между собой —
   * и опорой должно быть то, что он видит. Иначе вся отобранная дюжина
   * покрасится одинаково, и раскраска перестанет что-либо значить.
   */
  const bases = useMemo(() => {
    const out = {} as Record<ColKey, number | null>;
    for (const c of COLUMNS) out[c.key] = median(rows.map((r) => cellValue(r.campaign, c.key)));

    /* ═══════════════════════════════════════════════════════════════════
       ДРР И ЦЕНА ЗАКАЗА МЕРЯЮТСЯ ПРЕДЕЛОМ ЧЕЛОВЕКА, А НЕ МЕДИАНОЙ.

       Медиана отвечает «как эта кампания на фоне остальных». Вопрос,
       ради которого таблицу открывают, другой: «устраивает ли меня
       это». 28.08 владелец задал предел ДРР и написал: «цвета
       в кампаниях не обновились. Всё как было и осталось» — и был прав:
       столбец не знал о пределе вовсе.

       ЧЕМ МЕДИАНА ЗДЕСЬ ХУЖЕ ОТСУТСТВИЯ ЦВЕТА. В кабинете, где все
       кампании плохи одинаково, она красит их зелёными; где все хороши
       — половину красными. Цвет в обоих случаях уверенно говорит
       неправду, а человек по нему решает, что выключать.

       ЦЕЛИ НЕ ПРИШЛИ (ещё грузятся или запрос не удался) — пределом
       будет `null`, и `goalClass` промолчит. Молчание честнее возврата
       к медиане: цвет, меняющий смысл по ходу загрузки, хуже цвета,
       появившегося секундой позже. */
    out.drr = цели.data?.цели.drr ?? null;
    out.cpo = цели.data?.ценаЗаказа ?? null;
    return out;
  }, [rows, цели.data]);

  // Группы для верхней строки заголовка: подряд идущие колонки одной
  // группы сливаются в одну ячейку.
  const groups: Array<{ title: string; span: number }> = [];
  for (const c of cols) {
    const last = groups[groups.length - 1];
    if (last && last.title === c.group) last.span += 1;
    else groups.push({ title: c.group, span: 1 });
  }

  // Width and disclosure changes do not change campaign cells. Reuse their
  // elements so React can skip traversing every unchanged row again.
  // Child state and query subscriptions still update normally.
  const rowLayoutKey = startupRowWidthKey(видШирина);
  const mainRows = useMemo(() => new Map(rows.map(({ campaign: c, state }) => [c.id, (
<tr
                data-d="строка-кампании"
                /* ЧЕРТА ПОД СТРОКОЙ РИСУЕТСЯ КЛЕТКАМИ, А НЕ СТРОКОЙ.
                   Так в эталоне: `border-bottom` стоит на каждом `td`,
                   а у `tr` рамки нет вовсе. Разница видна замеру
                   (у нас была единица там, где у эталона ноль) и
                   проявляется на деле: у строки с `border-collapse`
                   рамка живёт по своим правилам и первой пропадает
                   при закреплённой шапке. */
                /* ОТМЕЧЕННАЯ СТРОКА — СВОЯ ЧАСТИЦА, вырезанная
                   из состояния «выделены строки» эталона
                   (`k-строка-кампании-выбрана`, подложка цвета марки).
                   `rk-row-picked` остаётся: он красит ещё и закреплённую
                   первую клетку, у которой своя непрозрачная подложка,
                   и держит цвет под указателем. */
                className={
                  `group rk-row rk-row-hair ${
                    picked.has(c.id) ? K.строкаКампанииВыбрана : K.строкаКампании
                  }` +
                  (picked.has(c.id) ? ' rk-row-picked' : '') +
                  (c.productCount > 0 ? ' rk-row-open' : '')
                }
              >
                {/* ПОЛОСА СОСТОЯНИЯ — слева от строки, 3 px, то же имя
                    состояния, что у лампы: два похожих правила однажды
                    разойдутся, одно не может. Цвет полосы лежит
                    в `rk-stripe-*` образца, а не вписан здесь. */}
                <td
                  className={`rk-col-1 rk-stripe rk-stripe-${toneOf(state.health)} rk-cell-y px-2 align-top`}
                  style={{ width: 'var(--col-campaign)' }}
                >
                  {/* НАЗВАНИЕ, СОСТОЯНИЕ И ТИП — ТРЕМЯ КОРОТКИМИ СТРОКАМИ,
                      а не одной длинной. Раньше всё это лежало вперемешку
                      в одну строку, и строки кампаний получались разной
                      высоты и разной ширины: глазу не за что зацепиться,
                      сравнивать невозможно. Ширина колонки закреплена
                      по той же причине. */}
                  <span className="flex items-start gap-2">
                    <input
                      className="mt-1"
                      type="checkbox"
                      checked={picked.has(c.id)}
                      onChange={() => onPick(c.id)}
                      aria-label={`Выделить ${c.name ?? c.advertId}`}
                    />
                    {/* ═══════════════════════════════════════════════
                        ЗАВЕРШЕНИЕ ВЕРНУЛОСЬ В ЭТОТ РЯД — ЗНАЧКОМ.

                        Передача 07.09.2026, раздел 4. Прежде оно уезжало
                        под название, потому что СЛОВО «Завершить»
                        растягивало верхнюю строку на треть. Значок
                        26 × 26 её не растягивает, а место ему именно
                        здесь: слева собраны органы управления показами
                        (светофор, тумблер), и завершение — крайняя
                        степень того же ряда.
                        ═══════════════════════════════════════════ */}
                    <Controls
                      base={base}
                      campaign={c}
                      state={state}
                      onDone={onDone}
                      compact
                      withFinish
                    />
                    {/* СНАЧАЛА ДАННЫЕ О КАМПАНИИ, ПОТОМ ФОТОГРАФИИ —
                        как в трекере. Читают строку слева направо,
                        и первым должно стоять то, чем строка называется:
                        имя, номер и тип. Фотографии — подробность,
                        она отвечает на второй вопрос, а не на первый. */}
                    <span className="flex min-w-0 flex-col">
                      {/* ═══════════════════════════════════════════
                          НАЗВАНИЕ КАМПАНИИ — ИСКЛЮЧЕНИЕ ИЗ ПЕРЕНОСА
                          (п. 4 круга 3, ответ 31).

                          Весь кабинет переносит длинные слова, чтобы
                          страница не ехала вбок. Строка таблицы — нет:
                          перенесённое имя делает строку двух- и
                          трёхэтажной, и сорок строк перестают
                          читаться столбцом. Здесь `truncate` —
                          одна строка с многоточием.

                          ПОЛНОЕ ИМЯ В ПОДСКАЗКЕ, А НЕ СОСТОЯНИЕ.
                          Раньше в `title` уходило состояние кампании
                          («Работает»), а оно и так написано плашкой
                          рядом. Обрезанное многоточием имя прочитать
                          было негде — а ищут строку как раз по имени.
                          ═══════════════════════════════════════ */}
                      <Link
                        to={withPeriod(`/rk/${wsId}/${cabinetId}/campaigns/${c.id}`, period)}
                        data-d="имя-кампании"
                        className={`truncate no-underline ${K.имяКампании}`}
                        title={c.name ?? `Кампания ${c.advertId}`}
                      >
                        {/* НОМЕР — МЕЛЬЧЕ НАЗВАНИЯ И ТАБЛИЧНЫМИ ЦИФРАМИ,
                            как в эталоне. Тем же кеглем он спорил
                            с названием за внимание: строка читается
                            как «Платье-миди льняное 24816304», а номер
                            нужен раз в месяц — сверить с письмом
                            Wildberries. */}
                        <b>{c.name ?? `Кампания ${c.advertId}`}</b>{' '}
                        {/* ПРОБЕЛ, А НЕ ОТСТУП: так в эталоне. С `ml-2`
                            ссылка выходила на восемь пикселей шире —
                            сверка намерила 282,4 против 274,5. */}
                        <span className="rk-text-xs rk-faint" style={{ fontVariantNumeric: 'tabular-nums' }}>
                          {c.advertId}
                        </span>
                      </Link>

                      {/* ТИП КАМПАНИИ ОТДЕЛЬНОЙ СТРОКОЙ. Он определяет,
                          что вообще можно настроить: у автоматической
                          ставка общая, и отключать площадку нечего. */}
                      {/* РОД — СЛОВОМ, ПЛОЩАДКИ — ПЛАШКАМИ. Длинная строка
                          из четырёх фактов читается один раз, а дальше
                          становится серым шумом: тридцать шесть таких
                          подряд прячут как раз то, ради чего подпись
                          заводилась. Способ оплаты и вид ставки — в
                          подсказке: они не меняются годами. */}
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                        <Kind
                          campaign={c}
                          группа={
                            /* ═══════════════════════════════════════
                               МЕТКА ГРУППЫ СТОИТ МЕЖДУ РОДОМ
                               И ПЛОЩАДКАМИ, А НЕ ПОСЛЕ НИХ.

                               Так в эталоне: `Автоматическая
                               [Рекомендации] [поиск] [рекомендации]`.
                               Стояла последней — и цветная плашка
                               группы оказывалась в конце ряда пустых,
                               а при переносе строки уезжала на вторую
                               вместе с «Завершить», то есть вставала
                               между «чем кампания является» и «что
                               с ней сделать».
                               ═══════════════════════════════════ */
                            именаГрупп !== undefined &&
                            c.groupId !== null &&
                            именаГрупп.get(c.groupId) !== undefined ? (
                              <МеткаГруппы имя={именаГрупп.get(c.groupId)!} />
                            ) : null
                          }
                        />
                        {/* ═══════════════════════════════════════════
                            МЕТКА ГРУППЫ — В ЭТОЙ ЖЕ СТРОКЕ, ПОСЛЕ РОДА.

                            Так в эталоне. Не отдельным столбцом:
                            столбец из тридцати шести повторяющихся
                            слов — это столбец, который перестают
                            читать, а нужен он ровно тогда, когда
                            человек смотрит «все кампании» и хочет
                            понять, чья эта строка.

                            Метки нет вовсе у кампании без группы:
                            «без группы» серым — это тридцать шесть
                            надписей ни о чём.
                            ═══════════════════════════════════════ */}
                      </span>

                      {/* ═══════════════════════════════════════════════
                          МЕТКИ ОПЫТОВ — СВОЕЙ СТРОКОЙ ПОД СВОЙСТВАМИ.

                          Передача 07.09.2026, раздел 2. Вид кампании,
                          группа и площадки — свойства: они не меняются
                          сами. Тест — событие: он идёт сейчас и кончится
                          сам. Вперемешку пять капсул читались как один
                          набор, и метка теста терялась среди «Поиск —
                          сезон» и «рекомендации».
                          ═══════════════════════════════════════════ */}
                      <МеткиТестов ставка={c.bidTest} фото={c.photoTest} />
                    </span>
                  </span>
                </td>

                {cols.map((col, i) => {
                  const first = i === 0 || cols[i - 1]!.group !== col.group;
                  {
                    /* ГРУППЫ РАЗВОДИТ ЗАЗОР, А НЕ ЛИНЕЙКА — как в эталоне,
                       где между столбцами стоит `gap: var(--sp-3)`
                       и ни одной вертикальной черты. */
                  }
                  /* ТОВАРЫ — СТОЛБЦОМ ПОСТОЯННОЙ ШИРИНЫ. Пока полоска
                     фотографий стояла внутри первой ячейки, кампания
                     с одним товаром и кампания с восемью разъезжались
                     по ширине, и колонка названий переставала быть
                     колонкой. */
                  if (col.key === 'products') {
                    return (
                      <td
                        key={col.key}
                        className={
                          'rk-cell-y px-2 align-top' + (first ? ' rk-grp-start' : '')
                        }
                        style={{ width: 'var(--col-products)' }}
                      >
                        <span className="flex flex-col items-start gap-0.5">
                          <ProductStrip nmIds={c.nmIds} count={c.productCount} />
                          <span className="rk-text-xs rk-faint">{c.productCount} шт</span>
                          {/* ═══════════════════════════════════════════
                              СПП И ОСТАТОК — ПОД ЧИСЛОМ ТОВАРОВ, И ТОЛЬКО
                              У КАМПАНИИ С ОДНИМ ТОВАРОМ.

                              Передача 07.09.2026, раздел 1. У кампании
                              из восьми товаров одно число на всех было бы
                              неправдой: у каждого свой остаток и своя
                              скидка. Такие показывают их в разборе.

                              ДНЕЙ ЗДЕСЬ НЕТ — место в колонке. «38 шт
                              на 71 день» отнимало место у названия
                              и налезало на плашку «Разбор». Запас в днях
                              читают, открыв кампанию; ЦВЕТ капсулы при
                              этом всё равно считается от дней, поэтому
                              кончающийся товар видно и в списке.
                              ═══════════════════════════════════════ */}
                          {/* ПРОПАЖУ ПОЛЯ ПРИВОДИМ К ПУСТОТЕ: `!== null`
                              пропускает `undefined`, и капсула роняла экран,
                              читая у него ступень. Довод — в `campaign.page.tsx`. */}
                          {((c.spp ?? null) !== null || (c.stock ?? null) !== null) && (
                            <span className="flex flex-wrap items-center" style={{ gap: 4 }}>
                              <Спп процент={c.spp} где="строка" когда={c.sppAt} />
                              <ЗапасКапсула запас={c.stock} где="строка" короткая />
                            </span>
                          )}
                        </span>
                      </td>
                    );
                  }
                  if (col.key === 'spark') {
                    return (
                      <td
                        key={col.key}
                        className={
                          'rk-cell-y px-2 align-top' + (first ? ' rk-grp-start' : '')
                        }
                        // Ширину держит сама «Динамика»: без неё столбец
                        // схлопывался по кампании без заказов, и подписи
                        // «ДРР ↑202 %» переносились по слогам
                        style={{ width: SPARK_WIDTH }}
                      >
                        <Spark values={c.spark} trend={c.trend} />
                      </td>
                    );
                  }
                  /* СТАВКА — ВИЛКОЙ, КОГДА КРАЯ РАЗНЫЕ. «255» там, где
                     половина товаров стоит на 90, отправит человека
                     поднимать не ту ставку. */
                  if (col.key === 'cpmBid') {
                    return (
                      <td
                        key={col.key}
                        className={
                          'whitespace-nowrap rk-cell-y px-2 text-right align-top tabular-nums rk-text-sm' +
                          (first ? ' rk-grp-start' : '')
                        }
                      >
                        <BidCellText min={c.cpmMin} max={c.cpmMax} />
                      </td>
                    );
                  }
                  if (col.kind === 'setting') {
                    return (
                      <td
                        key={col.key}
                        className={
                          'whitespace-nowrap p-1 pb-4 rk-text-sm align-top' +
                          (first ? ' rk-grp-start' : '')
                        }
                        /* ШИРИНА «ПРАВИЛ» ЗАКРЕПЛЕНА, как в эталоне
                           (`colDef`: 120px). Без неё столбец брал
                           ширину по самой длинной подписи лимита
                           («3 000/сут») и разъезжался от кабинета
                           к кабинету — а рядом с ним стоят числа,
                           которые сравнивают между строками. */
                        style={{ width: 'var(--col-rules)' }}
                      >
                        <RulesCell
                          campaign={c}
                          onOpen={(id, which) => setSetting({ id, which })}
                        />
                      </td>
                    );
                  }
                  if (col.key === 'budget') {
                    return (
                      <td
                        key={col.key}
                        className={
                          'whitespace-nowrap rk-cell-y px-2 text-right align-top tabular-nums' +
                          (first ? ' rk-grp-start' : '')
                        }
                      >
                        <BudgetCell
                          base={base}
                          campaign={c}
                          onDeposit={() => setDeposit(c.id)}
                          onDone={onDone}
                        />
                      </td>
                    );
                  }
                  const v = cellValue(c, col.key);
                  const paint = paintOf(col, v, bases[col.key]);
                  return (
                    <td
                      key={col.key}
                      /* Число вправо одними цифрами — `rk-num`; жирное —
                         то, ради чего таблицу открыли; приглушённое —
                         подробности рядом с ответом. Приглушение теперь
                         ступенью чернил, а не прозрачностью: выцветшее
                         число читается как «данные неточные». */
                      className={
                        'rk-num align-top rk-cell-y px-2' +
                        (first ? ' rk-grp-start' : '') +
                        (col.tone === 'strong' ? ' rk-strong' : '') +
                        (!paint && col.tone === 'muted' ? ' rk-dim' : '')
                      }
                      style={paint ? { color: TONE[paint] } : undefined}
                    >
                      {col.key === 'drr' ? <DrrValue value={c}>{render(v, col)}</DrrValue> : render(v, col)}
                    </td>
                  );
                })}
              </tr>
  )] as const)), [rows, picked, base, onPick, onDone, именаГрупп, cols, bases, rowLayoutKey]);

  return profileStartup('table.children', finishStartupWork('table.render', startupWorkStarted, (
    <div className="flex flex-col gap-2">
      {/* ═══════════════════════════════════════════════════════════════
          У ТАБЛИЦЫ СВОЯ ПРОКРУТКА, А НЕ СТРАНИЧНАЯ. Так в эталоне
          (`Bidra-Kabinet.dc.html`, строка 676):

              overflow:auto; max-height: calc(100vh - 232px);
              overscroll-behavior: contain

          ЗДЕСЬ СТОЯЛО `overflow-x-auto` БЕЗ ВЫСОТЫ, и это стоило
          владельцу обеих его жалоб на эту таблицу.

          Полоса прокрутки вбок у такой обёртки есть — но лежит она под
          ПОСЛЕДНЕЙ строкой списка. При сорока кампаниях до неё надо
          пролистать всю страницу, а вернувшись наверх — потерять её
          из виду. Ровно это владелец и назвал «нет нижнего бокового
          скрола»: полоса была, добраться до неё было нельзя.

          С собственной высотой полоса стоит у нижнего края таблицы
          и видна всегда, а шапка липнет к верху своего же окна,
          а не страницы: числа не остаются без подписей.

          `overscroll-behavior: contain` — чтобы прокрутка, дойдя
          до низа таблицы, не продолжалась страницей: человек листает
          список, а уезжает весь экран. */}
      <div
        className="rk-panel rk-table-scroll"
        data-hscroll="1"
        ref={видРеф}
        /* ЗАМЕР ПОВЕРХ ЧАСТИЦЫ, А НЕ ВМЕСТО НЕЁ. В `rk-table-scroll`
           осталась запись эталона (`100vh - 232px`): она работает
           до первого замера и в разборе разметки, где окна нет вовсе.
           Замер приходит следующим кадром и уточняет её по месту. */
        style={видВысота === null ? undefined : { maxHeight: видВысота }}
      >
        <table className={`rk-table ${K.таблицаКампаний}`} data-d="таблица-кампаний">
          {/* ЗАГОЛОВОК ЛИПНЕТ К ВЕРХУ. Сорок строк не помещаются в экран,
              а числа без подписи над ними — просто числа: прокрутив
              до середины, человек перестаёт понимать, что перед ним. */}
          <thead>
            {/* ВЕРХНЯЯ СТРОКА — ГРУППЫ. Без неё восемь числовых столбцов
                читаются как одна каша: «14,30» рядом с «17,70» ничего
                не значит, пока не видно, что первое про корзину,
                а второе про заказ. */}
            {/* СТРОКА ГРУПП — часть образца `rk-grp`: прописные
                вразрядку, 11/14, по центру над своими столбцами.
                Прежде кегль, разрядка и цвет стояли здесь числами
                и разошлись с остальными заголовками кабинета.

                ГРАНИЦА ГРУППЫ — ЧЕРТА ПОД НАЗВАНИЕМ, А НЕ СТОЛБИК МЕЖДУ
                СТОЛБЦАМИ. Вертикальных линеек эталон не знает ни в одной
                таблице: восемь столбиков сверху донизу превращали её
                в решётку, в которой тонули сами числа. Подчёркивание
                говорит ровно то же — докуда группа, — но идёт поперёк
                и строкам не мешает. */}
            <tr className="rk-grp">
              {/* НАД СТОЛБЦОМ НАЗВАНИЯ ГРУППЫ НЕТ: он и есть подпись
                  строки, а не показатель. Остальные заголовки групп
                  считаются по видимым колонкам — включая «Товары»
                  и «Динамику», которые стали колонками группы
                  «Кампания» и больше не прибиты к разметке. */}
              {/* ═══════════════════════════════════════════════════
                  ПЕРВАЯ ГРУППА НЕ ПОДПИСЫВАЕТСЯ ВОВСЕ — так в эталоне:
                  там над названием, товарами, динамикой и правилами
                  стоит ОДНА пустая ячейка на четыре столбца.

                  И это верно: «КАМПАНИЯ» над столбцом, который и есть
                  кампания, ничего не сообщает — только отнимает строку
                  у тех подписей, которые сообщают («Ставки», «Расход»,
                  «Трафик»). Сверку это тоже задевало: пустая ячейка
                  и подписанная — разной ширины. */}
              <th className="rk-col-1" colSpan={1 + (groups[0]?.title === ПЕРВАЯ_ГРУППА ? groups[0].span : 0)} />
              {groups.map((g, i) => {
                if (i === 0 && g.title === ПЕРВАЯ_ГРУППА) return null;
                return (
                  <th key={`${g.title}-${i}`} colSpan={g.span} className="rk-grp-line">
                    {/* «Расход, ₽» — единица в шапке, а не в каждой клетке.
                        Довод у `заголовокГруппы` в `columns.ts`. */}
                    {заголовокГруппы(g.title)}
                  </th>
                );
              })}
            </tr>
            <tr className="rk-hd">
              <th className="rk-col-1 rk-left">Кампания</th>
              {cols.map((c, i) => (
                <SortHead
                  key={c.key}
                  col={c}
                  first={i === 0 || cols[i - 1]!.group !== c.group}
                  active={sort === c.key}
                  desc={desc}
                  onSort={onSort}
                />
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map(({ campaign: c, state }) => (
              <Fragment key={c.id}>
              {/* СТРОКЕ С КНОПКОЙ «РАЗБОР» НУЖНО ЧУТЬ БОЛЬШЕ МЕСТА СНИЗУ —
                  кнопка сидит на её нижней границе. Строке без кнопки
                  оно не нужно, и раньше лишние шестнадцать пикселей
                  висели на всех подряд. */}
              {mainRows.get(c.id)}
              {/* КНОПКА РАЗБОРА — ПО ЦЕНТРУ НИЖНЕЙ ГРАНИЦЫ СТРОКИ,
                  как в трекере. Она относится ко всей строке, а не
                  к первой ячейке: спрятанная в углу рядом с названием,
                  она читается как часть названия и не находится. */}
              {c.productCount > 0 && (
                <tr>
                  <td colSpan={cols.length + LEAD_COLS} className="p-0">
                    {/* ПО ЦЕНТРУ НИЖНЕЙ ГРАНИЦЫ, как в трекере: кнопка
                        относится ко всей строке, и место ей — на её
                        краю, а не сбоку.

                        НАЕЗЖАТЬ ЕЙ БОЛЬШЕ НЕ НА ЧТО: строке добавлена
                        высота снизу. Первая попытка решить это опусканием
                        кнопки под границу увела её из центра — вернули
                        обратно и подвинули строку, а не кнопку. */}
                    <span
                      className="flex justify-center"
                      style={{ marginTop: -11, ...поВиду(видШирина) }}
                    >
                      <button
                        className={`rk-pill-open ${K.кнопкаРазбора}`}
                        data-d="кнопка-разбора"
                        onClick={() => toggleOpen(c.id)}
                        title="Кто из товаров приносит заказы, а кто тратит"
                      >
                        {/* СВОДКА ПРЯМО НА КНОПКЕ. «Разбор — 4 товаров»
                            не говорит ничего и открывать себя не зовёт;
                            «2 из 4 приносят заказы, 2 впустую» — говорит
                            всё и зовёт по делу. */}
                        {/* КАРЕТКА — ЗНАКОМ, А НЕ ЗНАЧКОМ: так в эталоне
                            (`'▾' : '▸'`). Двенадцатипиксельный контур
                            вместо шестипиксельного знака делал кнопку
                            на десять пикселей шире эталонной. */}
                        <span style={{ color: 'var(--ink-3)' }}>
                          {openIds.has(c.id) ? '▾' : '▸'}
                        </span>
                        <span>Разбор</span>
                        {/* ЧЁРТОЧКА, А НЕ ТИРЕ, как в эталоне: она делит
                            кнопку на «что это» и «что внутри», и сводка
                            справа читается числами, а не концом фразы. */}
                        <span
                          aria-hidden
                          style={{ width: 1, height: 12, background: 'var(--line)' }}
                        />
                        <b style={{ color: 'var(--good-ink)' }}>
                          {c.breakdown.works} из {c.breakdown.total} приносят заказы
                        </b>
                        {c.breakdown.wastes > 0 && (
                          <b style={{ color: 'var(--bad-ink)' }}>{c.breakdown.wastes} впустую</b>
                        )}
                      </button>
                    </span>
                  </td>
                </tr>
              )}
              {openIds.has(c.id) && (
                <tr>
                  {/* Разбор занимает всю ширину: он про одну кампанию,
                      и делить его на столбцы верхней таблицы незачем. */}
                  <td colSpan={cols.length + LEAD_COLS} style={{ background: 'var(--bg-sunken)' }}>
                    <div style={поВиду(видШирина)}>
                    <Breakdown
                      where={{ wsId, cabinetId }}
                      base={base}
                      campaignId={c.id}
                      campaignMetrics={c}
                      period={period}
                      open
                      onDone={onDone}
                    />
                    </div>
                  </td>
                </tr>
              )}
              </Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {deposit && (
        <Modal
          width={520}
          onClose={() => setDeposit(null)}
          /* ИМЯ УЗЛА — НА ЗАТЕМНЕНИИ, как у эталона. */
          имя="окно-пополнения"
          title={<b>Пополнить счёт кампании</b>}
        >
          <TopupNow
            base={base}
            campaignId={deposit}
            title={nameOf(rows, deposit)}
            balance={rows.find((r) => r.campaign.id === deposit)?.campaign.balance ?? null}
            suggested={rows.find((r) => r.campaign.id === deposit)?.campaign.features.topup.amount}
            onClose={() => setDeposit(null)}
            onDone={onDone}
          />
        </Modal>
      )}

      {setting && (
        <Modal
          width={setting.which === 'topup' ? 760 : setting.which === 'limits' ? 900 : 1040}
          onClose={() => setSetting(null)}
          /* ПОДВАЛ — ТОЛЬКО КОГДА РАЗДЕЛ ДАЛ ДЕЙСТВИЕ. У расписания
             своё сохранение внутри, и пустая кнопка «Сохранить» над ним
             обещала бы второе, несуществующее. */
          footer={
            действие === null ? undefined : (
              <>
                <button
                  className="rk-button-quiet"
                  disabled={действие.занят}
                  onClick={() => setSetting(null)}
                >
                  Отмена
                </button>
                <button
                  className="rk-button"
                  data-d="сохранить-настройку"
                  disabled={включить.isPending || действие.занят || действие.нельзя}
                  title={действие.подсказка}
                  onClick={действие.запустить}
                >
                  {действие.занят ? 'Сохраняю…' : (действие.надпись ?? 'Сохранить')}
                </button>
              </>
            )
          }
          title={
            <>
              {/* ═══════════════════════════════════════════════════════
                  ТУМБЛЕР В ЗАГОЛОВКЕ, КАК У КАРТОЧКИ КАМПАНИИ.

                  Без него окно было тупиком со своей же подсказкой:
                  раздел писал «числа заданы, а пополнение выключено —
                  включается тумблером в заголовке окна», и тумблера
                  в заголовке не было. Человек искал то, чего нет.

                  У расписания тумблера здесь нет: оно включается
                  самим расписанием внутри, и второй орган над ним
                  означал бы два выключателя на одну настройку. */}
              {setting.which !== 'schedule' && (
                <Switch
                  on={
                    setting.which === 'topup'
                      ? (rows.find((r) => r.campaign.id === setting.id)?.campaign.features.topup
                          .on ?? false)
                      : (rows.find((r) => r.campaign.id === setting.id)?.campaign.features.limits
                          .on ?? false)
                  }
                  disabled={включить.isPending || действие?.занят === true}
                  label={TITLES[setting.which]}
                  onChange={(next) =>
                    включить.mutate({ id: setting.id, which: setting.which, on: next })
                  }
                />
              )}
              <b>{TITLES[setting.which]}</b>
            </>
          }
        >
          {setting.which === 'schedule' ? (
            <HeatSchedule base={base} campaignId={setting.id} />
          ) : setting.which === 'topup' ? (
            <Topup
              base={base}
              campaignId={setting.id}
              managed={rows.find((r) => r.campaign.id === setting.id)?.campaign.managed ?? false}
              onDone={onDone}
              onДействие={setДействие}
              bare
            />
          ) : (
            <StopRules
              base={base}
              campaignId={setting.id}
              managed={rows.find((r) => r.campaign.id === setting.id)?.campaign.managed ?? false}
              onDone={onDone}
              onДействие={setДействие}
              only="limits"
            />
          )}
        </Modal>
      )}
    </div>
  )));
}

/**
 * Два оранжевых, и они означают разное.
 *
 * `warn` — «кончаются деньги на счёте» (жёлтый #fab219), `serious` —
 * «на грани» у лестницы целей (оранжевый #ec835a). Так их различает
 * эталон (`tone()`), и так же — кластеры. Собрать их в один цвет значит
 * ответить одной краской на два разных вопроса.
 */
const TONE: Record<'good' | 'bad' | 'warn' | 'serious', string> = {
  good: 'var(--good-ink)',
  bad: 'var(--bad-ink)',
  warn: 'var(--warn-ink)',
  serious: 'var(--serious-ink)',
};

function SortHead({
  col,
  first,
  active,
  desc,
  onSort,
}: {
  col: Col;
  first: boolean;
  active: boolean;
  desc: boolean;
  onSort: (key: SortKey) => void;
}) {
  /**
   * ЧИСЛА ВПРАВО, ОСТАЛЬНОЕ ВЛЕВО — заголовок стоит над своим
   * содержимым, а не наоборот: под «Правилами» значки, под «Товарами»
   * фотографии, и правый край этих столбцов пуст.
   */
  const own = col.kind === 'setting' || col.kind === 'cell';
  /**
   * НЕСОРТИРУЕМЫЙ ЗАГОЛОВОК — НЕ КНОПКА.
   *
   * Кнопка обещает, что нажатие что-то сделает. Над фотографиями
   * и значками правил она не делает ничего — и человек, нажавший
   * дважды, идёт искать поломку. Раньше кнопкой был каждый заголовок,
   * включая «Правила».
   */
  if (own) {
    return (
      <th className={'rk-left ' + (first ? 'rk-grp-start ' : '')} title={col.hint}>
        {col.title}
      </th>
    );
  }
  return (
    // Вид заголовка — из образца: кегль, цвет, поля и подчёркнутая
    // черта снизу задаёт `rk-hd`, начало группы — отступ `rk-grp-start`,
    // а выбранный столбец — `rk-sorted`.
    <th className={(first ? 'rk-grp-start ' : '') + (active ? 'rk-sorted' : '')}>
      {/* ЗАГОЛОВОК И ЕСТЬ КНОПКА СОРТИРОВКИ. Отдельный выпадающий список
          «сортировать по…» существует и остаётся, но искать в нём то,
          на что можно просто нажать, никто не станет. */}
      <button title={col.hint} onClick={() => onSort(col.key)}>
        {col.title}
        {active && <span>{desc ? ' ↓' : ' ↑'}</span>}
      </button>
    </th>
  );
}

/**
 * Три настройки одной ячейкой: расписание, автопополнение, лимит.
 *
 * ═══════════════════════════════════════════════════════════════════
 * ПОЧЕМУ ЗНАЧКИ, А НЕ ТРИ СТОЛБЦА СЛОВАМИ.
 *
 * Столбцов было три, и каждый занимал ширину своего заголовка —
 * «Автопополнение» это четырнадцать знаков. В тридцати строках
 * из тридцати шести они сообщали одно и то же: «круглосуточно»,
 * «выключено», «без ограничений». То есть треть ширины экрана уходила
 * на сообщение, что НИЧЕГО не настроено, причём набранное тем же
 * кеглем, что и деньги.
 *
 * Теперь выключенное — бледный значок шириной в букву, а место занимает
 * только заданное. Строка, где ничего не настроено, молчит; строка,
 * где настроено, видна сразу — цветом и числом.
 * ═══════════════════════════════════════════════════════════════════
 *
 * ЗНАЧОК БЕЗ ПОДПИСИ — ЗАГАДКА, ПОЭТОМУ ПОДПИСЬ ЕСТЬ ВСЕГДА: у заданного
 * она стоит рядом числом, у выключенного живёт в подсказке при наведении.
 * Значки выбраны непохожими друг на друга по силуэту, а не по смыслу:
 * различают их боковым зрением, пробегая столбец глазами.
 */
type Which = 'topup' | 'limits' | 'schedule';

const TITLES: Record<Which, string> = {
  topup: 'Автопополнение бюджета',
  limits: 'Лимит бюджета',
  schedule: 'Расписание показов',
};

/**
 * Значок и то, как называется настройка в подсказке.
 *
 * ЗНАЧКИ НАРИСОВАНЫ, А НЕ НАБРАНЫ БУКВАМИ. Здесь стояли `◷`, `⇡` и `⊘` —
 * знаки юникода, которые рисует ШРИФТ: у одного человека кружок часов
 * тонкий, у другого жирный, у третьего вместо знака пустой прямоугольник.
 * Ни толщина штриха, ни выравнивание по строке нам при этом
 * не подчинялись.
 */
const RULES: Array<{ which: Which; Icon: typeof IconClock; name: string }> = [
  { which: 'schedule', Icon: IconClock, name: 'Расписание показов' },
  { which: 'topup', Icon: IconTopup, name: 'Автопополнение' },
  { which: 'limits', Icon: IconCap, name: 'Лимит расхода' },
];

function RulesCell({
  campaign,
  onOpen,
}: {
  campaign: CampaignRef;
  onOpen: (campaignId: string, which: Which) => void;
}) {
  return profileStartup('rules.children', (
    <span className="flex items-center gap-0.5">
      {RULES.map((r) => {
        const on = campaign.features[r.which].on;
        return (
          <button
            key={r.which}
            /* ПЛАШКА ПРАВИЛА — СВОЯ ЧАСТЬ ОБРАЗЦА, А НЕ ПЛАШКА-ЯРЛЫК.
               У ярлыка круглый край и постоянный фон, у правила —
               прямой угол, фон только при наведении и число теми же
               цифрами, что в соседних столбцах. Прежде здесь стоял
               ярлык, и столбец настроек читался как столбец меток.

               ВЫКЛЮЧЕННОЕ БЛЕДНОЕ, А НЕ СЕРОЕ: оно должно оставаться
               видимым — иначе по нему не нажать, — но не спорить
               за внимание с настроенным. Степень бледности взята
               из образца (`rk-rule-off`), а не подобрана на глаз. */
            className={'rk-rule ' + (on ? 'rk-rule-on' : 'rk-rule-off')}
            // ПОДСКАЗКА ГОВОРИТ ВСЁ ЦЕЛИКОМ. На экране число без единиц
            // («40», «1 000»), потому что столбец узкий; полная фраза —
            // здесь, и она же объясняет, что будет по нажатию.
            title={`${r.name}: ${settingText(campaign, r.which)}. Нажмите, чтобы ${on ? 'изменить' : 'настроить'}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onOpen(campaign.id, r.which);
            }}
          >
            <r.Icon size={13} />
            {on && <span>{shortSetting(campaign, r.which)}</span>}
          </button>
        );
      })}
    </span>
  ));
}

/**
 * Остаток счёта с двумя кнопками: положить денег и спросить заново.
 *
 * ЧИСЛО ЗДЕСЬ СТАРЕЕТ ЗА МИНУТЫ — кампания тратит непрерывно, а список
 * приносит остаток тем, каким его запомнила последняя выгрузка. Отсюда
 * «↺»: спросить у Wildberries прямо сейчас, не дожидаясь часа.
 *
 * «+» СТОИТ РЯДОМ С ЧИСЛОМ, А НЕ В НАСТРОЙКАХ, потому что решение
 * «положить» принимается ровно в тот миг, когда человек увидел остаток.
 * Уводить его за этим в другое место значит терять и место в списке,
 * и само число, с которым он сравнивал.
 *
 * ОБЕ КНОПКИ ВИДНЫ ВСЕГДА, ХОТЯ И БЛЕДНО. Показывать их только
 * при наведении — приём опрятный и ровно поэтому негодный: о кнопке,
 * которой не видно, не знают.
 */
function BudgetCell({
  base,
  campaign,
  onDeposit,
  onDone,
}: {
  base: string;
  campaign: CampaignRef;
  onDeposit: () => void;
  onDone: (text: string) => void;
}) {
  const qc = useQueryClient();

  const refresh = useMutation({
    mutationFn: () =>
      api<{ balance: number | null; failed: boolean; description: string }>(
        `${base}/campaigns/${campaign.id}/balance?refresh=1`,
      ),
    onSuccess: async (r) => {
      // ОТКАЗ НАЗЫВАЕТСЯ ОТКАЗОМ. Молча оставить прежнее число
      // по нажатию «обновить» — значит показать вчерашнее как сегодняшнее.
      if (r.failed) {
        onDone('Wildberries не ответил — остаток показан прежний, тот же, что был до нажатия.');
      }
      for (const key of ['campaigns', 'balance']) {
        await qc.invalidateQueries({ queryKey: [key] });
      }
    },
    onError: (e) => onDone(e instanceof Error ? e.message : 'Не вышло'),
  });

  const v = campaign.balance;
  const paint = v === null ? null : v <= 0 ? 'bad' : v < 500 ? 'warn' : null;

  return profileStartup('budget.children', (
    /* ═══════════════════════════════════════════════════════════════
       ЗНАЧКИ ЗДЕСЬ — ЧАСТИЦЕЙ ЭТАЛОНА, А НЕ КАПСУЛОЙ `rk-chip`.

       `rk-chip` — это капсула высотой 22 с полями по восемь: она
       для надписей вроде «под управлением», и значок в ней выходит
       двадцать девять пикселей шириной вместо двадцати. Две такие
       капсулы делали столбец «Бюджет» на двадцать девять пикселей
       шире эталонного — и это была вся оставшаяся разница ширины
       таблицы после того, как сошлись столбцы и числа.

       ПРОЗРАЧНОСТЬ .45 ТОЖЕ ИЗ ЭТАЛОНА (было .35), а проявление
       по наведению на строку остаётся нашим: в стенде наведения нет.
       ═══════════════════════════════════════════════════════════ */
    <span className={K.ячейкаБюджета}>
      <button
        className={`${K.значокВБюджете} group-hover:opacity-100`}
        title="Положить денег на счёт этой кампании прямо сейчас"
        aria-label="Пополнить счёт кампании"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onDeposit();
        }}
      >
        <IconPlus size={13} />
      </button>
      <button
        className={`${K.значокВБюджете} group-hover:opacity-100 disabled:opacity-20`}
        disabled={refresh.isPending}
        title="Спросить остаток у Wildberries заново"
        aria-label="Обновить остаток"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          refresh.mutate();
        }}
      >
        <IconRefresh
          size={13}
          style={refresh.isPending ? { animation: 'rk-spin 1s linear infinite' } : undefined}
        />
      </button>
      <b
        style={{
          color: paint ? TONE[paint] : undefined,
          fontWeight: 400,
          // СТАРОЕ ЧИСЛО ПОКАЗЫВАЕМ БЛЕДНЕЕ. Оно не враньё, но и не
          // «сейчас»; разница должна быть видна без чтения подписи.
          opacity: stale(campaign.balanceAt) ? 0.55 : 1,
        }}
        title={`Остаток на счёте: ${measuredAt(campaign.balanceAt)}`}
      >
        {render(v, {
          kind: 'money',
          пусто: 'Остаток счёта ещё ни разу не выгружался — обычно он приходит в первую минуту после подключения',
        })}
      </b>
    </span>
  ));
}

/**
 * Возраст замера остатка.
 *
 * ПОЛЧАСА — УЖЕ «ДАВНО»: за это время работающая кампания успевает
 * потратить сотни. Тот же порог стоит на сервере (`STALE_MINUTES`),
 * и разъедься они — экран назвал бы свежим то, что сервер считает
 * устаревшим.
 */
const STALE_MINUTES = 30;

function minutesSince(at: string | null): number | null {
  if (at === null) return null;
  const t = new Date(at).getTime();
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 60_000));
}

function stale(at: string | null): boolean {
  const m = minutesSince(at);
  // Ни разу не измеряли — это не «свежо». Показать такое число ярким
  // значило бы выдать неизвестность за сегодняшний замер.
  return m === null || m >= STALE_MINUTES;
}

function measuredAt(at: string | null): string {
  const m = minutesSince(at);
  if (m === null) return 'ни разу не измерялся — нажмите ↺';
  if (m < 1) return 'измерен только что';
  if (m < 60) return `измерен ${m} мин назад`;
  const h = Math.floor(m / 60);
  if (h < 24) return `измерен ${h} ч назад`;
  return `измерен ${Math.floor(h / 24)} сут назад`;
}

/** Название кампании для заголовка окна. */
function nameOf(rows: Row[], id: string): string {
  const c = rows.find((r) => r.campaign.id === id)?.campaign;
  return c ? (c.name ?? `Кампания ${c.advertId}`) : '';
}

/**
 * Род кампании и площадки.
 *
 * ПЛОЩАДКИ ПЛАШКАМИ, ПОТОМУ ЧТО ИХ СРАВНИВАЮТ ВЗГЛЯДОМ ПО СТОЛБЦУ.
 * «поиск» и «поиск + рекомендации» словами в конце длинной фразы
 * различаются чтением; плашками — с одного взгляда, а именно так
 * на них и смотрят: ища, где включены рекомендации.
 */
function Kind({ campaign, группа }: { campaign: CampaignRef; группа?: React.ReactNode }) {
  const t = campaignType(campaign);
  return (
    <span className="flex flex-wrap items-center gap-1" title={t.hint}>
      <b className={K.родКампании}>{t.kind}</b>
      {группа}
      {t.places.map((p) => (
        <span key={p} className={K.площадкаКампании}>
          {p}
        </span>
      ))}
    </span>
  );
}

/**
 * Что написано в ячейке настройки.
 *
 * ЗНАЧЕНИЕ, А НЕ ФАКТ ВКЛЮЧЁННОСТИ. «Расписание: вкл» не отвечает
 * ни на один вопрос; «40 ч в неделю» отвечает сразу — и на «работает ли»,
 * и на «сколько». Ровно за этим настройки и вынесены в столбцы:
 * чтобы читаться, а не открываться.
 */
function settingText(c: CampaignRef, which: Which): string {
  if (which === 'topup') {
    const t = c.features.topup;
    return t.on && t.amount && t.below
      ? `${money.format(t.amount)} ₽, если меньше ${money.format(t.below)} ₽`
      : 'выключено';
  }
  if (which === 'limits') return c.features.limits.on ? limitText(c) : 'без ограничений';

  const s = c.features.schedule;
  if (!s.on) return 'круглосуточно';
  return `${s.hours} ч в неделю`;
}

function limitText(c: CampaignRef): string {
  const l = c.features.limits;
  const parts: string[] = [];
  if (l.day) parts.push(`${money.format(l.day)} ₽/сут`);
  if (l.total) parts.push(`${money.format(l.total)} ₽ всего`);
  return parts.length ? parts.join(' · ') : 'включён без чисел';
}

/**
 * То же самое в две-три буквы — рядом со значком.
 *
 * ЧИСЛО БЕЗ ЕДИНИЦ, И ЭТО НЕ НЕБРЕЖНОСТЬ. Единицы у каждой настройки
 * свои и не меняются: часы у расписания, рубли у денег. Повторять их
 * в каждой из тридцати шести строк значит потратить ширину столбца
 * на то, что человек узнал один раз. Полная фраза — в подсказке,
 * и она приходит по наведению, то есть ровно тогда, когда нужна.
 */
function shortSetting(c: CampaignRef, which: Which): string {
  if (which === 'schedule') return `${c.features.schedule.hours} ч`;
  if (which === 'topup') {
    const t = c.features.topup;
    return t.amount ? money.format(t.amount) : 'вкл';
  }
  const l = c.features.limits;
  if (l.day) return `${money.format(l.day)}/сут`;
  if (l.total) return money.format(l.total);
  return 'вкл';
}

/** Unrelated page queries do not rebuild the campaign table. Local state and query updates still render normally. */
export const StableCampaignTable = memo(CampaignTable);
